import { cva } from 'class-variance-authority'
import {
  type LucideIcon,
  ShieldAlert,
  ShieldCheck,
  ShieldMinus,
  ShieldPlus,
  ShieldQuestion,
} from 'lucide-react'

import { ControlStatus } from '@/gen/controls/v1/control_status_pb'

import { cn } from '@/lib/ui/style-helpers'

const controlStatusIconVariants = cva('rounded', {
  variants: {
    backgroundStyle: {
      subtle: 'p-1 size-5.5',
      ghost: 'bg-transparent',
    },
    backgroundColor: {
      [ControlStatus.GAP]: 'bg-main-background-subtle-semantic-danger',
      [ControlStatus.VALIDATED]: 'bg-main-background-subtle-semantic-success',
      [ControlStatus.NO_EVIDENCE]: 'bg-main-background-subtle-semantic-warning',
      [ControlStatus.NOT_APPLICABLE]: 'bg-main-background-muted',
      [ControlStatus.ATTESTED]: 'bg-main-background-subtle-semantic-accent',
      [ControlStatus.NOT_EVALUATED]: 'bg-transparent',
      [ControlStatus.UNSPECIFIED]: 'bg-transparent',
    },
  },
})

const statusIcons: Record<ControlStatus, { Icon: LucideIcon; className: string } | undefined> = {
  [ControlStatus.GAP]: { Icon: ShieldAlert, className: 'text-main-foreground-danger' },
  [ControlStatus.VALIDATED]: { Icon: ShieldCheck, className: 'text-main-foreground-success' },
  [ControlStatus.ATTESTED]: { Icon: ShieldPlus, className: 'text-main-foreground-accent' },
  [ControlStatus.NO_EVIDENCE]: { Icon: ShieldQuestion, className: 'text-main-foreground-warning' },
  [ControlStatus.NOT_APPLICABLE]: {
    Icon: ShieldMinus,
    className: 'text-main-foreground-secondary',
  },
  [ControlStatus.NOT_EVALUATED]: undefined,
  [ControlStatus.UNSPECIFIED]: undefined,
}

type ControlStatusIconProps = {
  status: ControlStatus
  backgroundStyle?: 'subtle' | 'ghost'
}

export const ControlStatusIcon = ({
  status,
  backgroundStyle = 'ghost',
}: ControlStatusIconProps) => {
  const icon = statusIcons[status]
  if (!icon) {
    return null
  }

  const { Icon, className } = icon

  return (
    <Icon
      className={cn(
        'size-4',
        className,
        controlStatusIconVariants({ backgroundStyle, backgroundColor: status }),
      )}
    />
  )
}
